import React, { Dispatch } from "react";
import { RouteComponentProps } from "react-router-dom";
import { showSnack as ShowSnack } from "react-redux-snackbar";
import { connect } from "react-redux";
import { SettingCategory, SettingsContainer } from "../../../Layout";
import {
  LoadingState,
  ResponseError,
  ErrorDisplay,
  LoadingDisplay,
  ApiRequest,
  ForbiddenDisplay
} from "../../../Common";
import { UserSettings } from "../../Models/UserSettings";
import { endpoints } from "../../Constants/endpoints";
import ResponseHandler from "../../../App/ResponseHandler";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import { StatusCode } from "../../../App/Enums/StatusCode";
import { NotificationSettings } from "./NotificationSettings";

type TParams = {
  username: string;
};

type State = {
  settings?: UserSettings;
  settingCategories: SettingCategory[];
  selectedCategory: SettingCategory;
  loadingState: LoadingState;
  loadingError: ResponseError;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  showSnack: (uniqueId: any, args: any) => dispatch(ShowSnack(uniqueId, args))
});

const initialState = {
  settingCategories: [SettingCategory.NotificationSettings],
  selectedCategory: SettingCategory.NotificationSettings,
  loadingState: {
    errored: false,
    isLoaded: false,
    isLoading: false
  },
  loadingError: {
    errorDescription: "",
    errorResult: ""
  }
};

class UserSettingsContainer extends React.Component<
  RouteComponentProps<TParams> & DispatchProps,
  State
> {
  constructor(props: RouteComponentProps<TParams> & DispatchProps) {
    super(props);

    this.state = initialState;
  }

  componentDidMount = () => {
    const { match } = this.props;
    this.getUserSettings(match.params.username);
  };

  componentDidUpdate = (prevProps: RouteComponentProps<TParams> & DispatchProps) => {
    const { match } = this.props;
    if (prevProps.match.params.username !== match.params.username) {
      this.setState(initialState, () => this.getUserSettings(match.params.username));
    }
  };

  getUserSettings = (username: string) => {
    ApiRequest.fromPath(endpoints.settings.getSettings(username))
      .withAuthHeader()
      .get()
      .then(response => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult<UserSettings>) => {
            this.setState((prevState: State) => ({
              settings: result.payload,
              loadingState: { ...prevState.loadingState, isLoaded: true, isLoading: false }
            }));
          },
          result => {
            this.setLoadingError(response.status, result.error.message, "Failed to get settings");
          },
          () => {
            this.setLoadingError(
              response.status,
              "An unknown error has occured",
              "Failed to get settings"
            );
          }
        );
      })
      .catch(() => {
        this.setLoadingError(
          StatusCode.Status503ServiceUnavailable,
          "Unable to connect to server",
          "Failed to get settings"
        );
      });
  };

  setUserSetting = (key: string, value: string, action: () => void) => {
    const { match } = this.props;
    ApiRequest.fromPath(endpoints.settings.setSetting(match.params.username))
      .withAuthHeader()
      .withJson({ key, value })
      .put()
      .then(response => {
        ResponseHandler.handle(
          response,
          () => {
            action();
          },
          result => {
            this.setLoadingError(
              response.status,
              result.error.message,
              "Failed to set setting",
              true
            );
          },
          () => {
            this.setLoadingError(
              response.status,
              "An unknown error has occured",
              "Failed to set setting",
              true
            );
          }
        );
      })
      .catch(() => {
        this.setLoadingError(
          StatusCode.Status503ServiceUnavailable,
          "Unable to connect to server",
          "Failed to get settings",
          true
        );
      });
  };

  setLoadingError = (
    errorCode: number,
    errorDescription: string,
    errorResult: string,
    shouldShowSnack: boolean = false
  ) => {
    this.setState(
      (prevState: State) => ({
        loadingState: {
          ...prevState.loadingState,
          errored: !shouldShowSnack,
          isLoading: false
        },
        loadingError: {
          errorCode,
          errorDescription,
          errorResult
        }
      }),
      () => {
        if (shouldShowSnack) {
          this.showErrorSnackbar(errorCode, errorDescription, errorResult);
        }
      }
    );
  };

  onSettingCategoryClick = (category: SettingCategory) => {
    this.setState({
      selectedCategory: category
    });
  };

  showErrorSnackbar = (errorCode: number, errorDescription: string, reason: string) => {
    const { showSnack } = this.props;
    showSnack("FEEDBACK_CREATE_BUG_REPORT", {
      label: `${reason}: ${errorDescription} (Error Code ${errorCode})`,
      timeout: 2500
    });
  };

  render() {
    const {
      loadingState,
      loadingError,
      settings,
      selectedCategory,
      settingCategories
    } = this.state;
    if (loadingState.errored) {
      if (loadingError.errorCode === StatusCode.Status403Forbidden) {
        return <ForbiddenDisplay description={loadingError.errorDescription} />;
      }

      return <ErrorDisplay {...loadingError} />;
    }

    if (loadingState.isLoaded && !!settings) {
      let settingPage;

      switch (selectedCategory) {
        case SettingCategory.NotificationSettings:
          settingPage = (
            <NotificationSettings settings={settings} onSetSetting={this.setUserSetting} />
          );
          break;
        default:
          return (
            <ErrorDisplay
              errorResult="Failed to load settings"
              errorDescription={`${selectedCategory} is not a valid setting category`}
            />
          );
      }

      return (
        <SettingsContainer
          title="User Settings"
          selectedCategory={selectedCategory}
          settingCategories={settingCategories}
          onCategoryChange={this.onSettingCategoryClick}
        >
          {settingPage}
        </SettingsContainer>
      );
    }

    return <LoadingDisplay />;
  }
}

export default connect(
  null,
  mapDispatchToProps
)(UserSettingsContainer);
